import React, { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';

import { categories, TOURNAMENT_RUN_OPTIONS } from 'constants/tests';
import { useQueryParams } from 'hooks/useQueryParams';
import { useFreecodeContextRun, useFreecodeContextData, useFreecodeContextLayout } from 'hooks/useContext';
import { tabLabels, identifiers, buttonIds, editorTypes } from '../../FreecodeLayout/FreecodeLayoutConstants';
import FreecodeTabSelector from 'components/FreecodePage/FreecodeTabSelector/FreecodeTabSelector';
import { OptionContainer } from 'components/FreecodePage/FreecodeTabSelector/styles';
import { Checkbox } from 'components/Checkbox/Checkbox';
import { RectangleButton } from 'components/Button/RectangleButton';
import RectangleDropdownButton from 'components/Button/RectangleDropdownButton';
import Console from './Console';
import Score from './Score';
import YesNoModal from 'components/Modals/yesNoModal';
import LiveCodingStatus from './LiveCodingStatus';

const submitEditors = [editorTypes.contest, editorTypes.freecode, editorTypes.freecodeTrial];

export default function FreecodeConsoleAndScore() {
  const navigate = useNavigate();
  const { courseId } = useQueryParams();

  const { setDisplaySubmit, editorType } = useFreecodeContextLayout();
  const { test } = useFreecodeContextData();
  const {
    runFunction,
    isRunning,
    runHidden,
    setRunHidden,
    saveOnRun,
    toggleSaveOnRun,
    scoreTabScore,
    runAgainst,
    setRunAgainst,
  } = useFreecodeContextRun();

  const [nextButtonSettings, setNextButtonSettings] = useState({});
  const [displayNextModal, setDisplayNextModal] = useState(false);

  const runCode = () => runFunction && runFunction();
  const submit = () => setDisplaySubmit(true);

  useEffect(() => {
    if (editorType !== editorTypes.exercise) return;
    let link = '/exercises';
    let buttonText = 'More Exercises';
    let buttonTitle = 'Click here to return to the exercise index page';
    let modalTitle = 'More Exercises';
    let modalText = 'Do you want to return to the exercise index page?';
    if (courseId && test.nextExercise) {
      link = `/exercise/${test.nextExercise}?courseId=${courseId}`;
      buttonText = 'Next Exercise';
      buttonTitle = 'Click here to go to the next course module';
      modalTitle = 'Next Exercise';
      modalText = 'Do you want to continue to the next exercise that is part of the course?';
    } else if (courseId) {
      link = `/courses/${courseId}`;
      buttonText = 'Course Page';
      buttonTitle = 'Click here to go to the course landing page';
      modalTitle = 'Course Page';
      modalText = 'Do you want to return to the course page?';
    }
    setNextButtonSettings({ link, buttonText, buttonTitle, modalTitle, modalText, setDisplayNextModal });
  }, [courseId, editorType, test.nextExercise]);

  const getRunButton = () => {
    if (editorType === editorTypes.liveCoding) return <LiveCodingStatus />;
    if (editorType === editorTypes.contest && test.isTournament)
      return (
        <RectangleDropdownButton
          id={buttonIds.runButton}
          label="Run code"
          options={TOURNAMENT_RUN_OPTIONS}
          value={runAgainst}
          disabled={isRunning}
          onClick={runCode}
          onChange={(response) => {
            setRunAgainst(response.value);
            localStorage.setItem('fsEditor-runAgainst', response.value);
          }}
        />
      );
    return (
      <RectangleButton id={buttonIds.runButton} small onClick={runCode} disabled={isRunning}>
        Run Code
      </RectangleButton>
    );
  };

  const getHeaderOptions = () => (
    <OptionContainer>
      {[editorTypes.freecodeInspect, editorTypes.creator, editorTypes.creatorInspect].includes(editorType) &&
        test.category !== categories.EXERCISE.category && (
          <Checkbox checked={runHidden} label="Run Hidden Levels" onChange={() => setRunHidden(!runHidden)} />
        )}

      {editorType === editorTypes.creator && (
        <Checkbox checked={saveOnRun} label="Save On Run" onChange={toggleSaveOnRun} />
      )}

      {getRunButton()}

      {submitEditors.includes(editorType) && (
        <RectangleButton small onClick={submit} id={buttonIds.submitButton}>
          Submit
        </RectangleButton>
      )}

      {displayNextModal && (
        <YesNoModal
          confirm={(yes) => {
            setDisplayNextModal(false);
            if (yes) navigate(nextButtonSettings.link);
          }}
          modalInfo={{
            title: nextButtonSettings.modalTitle,
            body: nextButtonSettings.modalText,
          }}
        />
      )}
    </OptionContainer>
  );

  return (
    <FreecodeTabSelector
      items={[
        {
          icon: tabLabels[identifiers.console].icon,
          label: tabLabels[identifiers.console].label,
          tabId: tabLabels[identifiers.console].tabId,
          component: <Console runFunction={runFunction} nextButtonSettings={nextButtonSettings} />,
        },
        {
          icon: tabLabels[identifiers.score].icon,
          label: `${tabLabels[identifiers.score].label} ${scoreTabScore}`,
          tabId: tabLabels[identifiers.score].tabId,
          component: <Score />,
        },
      ]}
      headerComponents={getHeaderOptions()}
    />
  );
}
